import React, { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  BookOpen,
  Wifi,
  WifiOff,
  FileText,
  BookMarked,
  Plus,
  Eye,
  Edit,
  Trash2
} from 'lucide-react'
import { useFirebase } from '../contexts/FirebaseContext'
import OnlineTestManagement from '../components/OnlineTestManagement'
import { useDialog } from '../components/DialogProvider'
import { SkeletonOfflineTests } from '../components/Skeleton'

const Home = () => {
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState('offline')
  const [homeContents, setHomeContents] = useState([])
  const [filteredContents, setFilteredContents] = useState([])
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { getHomeContents, getTestQuestions, deleteHomeContentCascade, syncAllTestCounts } = useFirebase()
  const dialog = useDialog()

  // Confirmation modal state for edit/delete actions
  const [confirmState, setConfirmState] = useState({ open: false, mode: null, content: null })

  // Date filter state
  const [dateFilter, setDateFilter] = useState('all') // all, today, week, month, year, custom
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')

  // Check URL parameters to set active tab
  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab === 'online') {
      setActiveTab('online')
    }
  }, [searchParams])

  // Utility function to safely format dates as DD-MM-YYYY
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A'

    try {
      let date
      if (dateValue instanceof Date) {
        date = dateValue
      } else if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate()
      } else {
        date = new Date(dateValue)
      }

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date'
      }
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}-${month}-${year}`
    } catch (error) {
      console.error('Error formatting date:', error)
      return 'Invalid Date'
    }
  }

  useEffect(() => {
    if (activeTab === 'offline') {
      loadHomeContents()
      // Auto-sync test counts on first load (one-time fix for existing data)
      const hasAutoSynced = localStorage.getItem('testCountsAutoSynced')
      if (!hasAutoSynced) {
        console.log('🔄 Auto-syncing test counts for the first time...')
        syncAllTestCounts()
          .then(results => {
            const successCount = results.filter(r => r.status === 'success').length
            console.log(`✅ Auto-synced ${successCount} documents`)
            localStorage.setItem('testCountsAutoSynced', 'true')
          })
          .catch(error => {
            console.error('Auto-sync failed:', error)
          })
      }
    }
  }, [activeTab])

  const loadHomeContents = async () => {
    try {
      setLoading(true)
      const contents = await getHomeContents()

      // Get test counts for each content (count only tests with at least one question)
      const contentsWithTestCounts = await Promise.all(
        contents.map(async (content) => {
          try {
            const tests = await getTestQuestions(content.id)
            // Only count tests that have at least one question
            const validTests = (tests || []).filter(test => Array.isArray(test.questions) && test.questions.length > 0)
            return {
              ...content,
              testCount: validTests.length
            }
          } catch (error) {
            return {
              ...content,
              testCount: 0
            }
          }
        })
      )
      setHomeContents(contentsWithTestCounts)
      setFilteredContents(contentsWithTestCounts)
    } catch (error) {
      console.error('Error loading home contents:', error)
      setHomeContents([])
      setFilteredContents([])
    } finally {
      setLoading(false)
    }
  }

  // Apply date filters
  useEffect(() => {
    applyDateFilter()
  }, [homeContents, dateFilter, customStartDate, customEndDate])

  const applyDateFilter = () => {
    if (dateFilter === 'all') {
      setFilteredContents(homeContents)
      return
    }

    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    const filtered = homeContents.filter(content => {
      if (!content.createdAt) return false

      let contentDate
      try {
        if (content.createdAt instanceof Date) {
          contentDate = content.createdAt
        } else if (content.createdAt.toDate && typeof content.createdAt.toDate === 'function') {
          contentDate = content.createdAt.toDate()
        } else {
          contentDate = new Date(content.createdAt)
        }

        if (isNaN(contentDate.getTime())) return false
      } catch (error) {
        return false
      }

      const contentDateOnly = new Date(contentDate.getFullYear(), contentDate.getMonth(), contentDate.getDate())

      switch (dateFilter) {
        case 'today':
          return contentDateOnly.getTime() === today.getTime()

        case 'week': {
          const weekAgo = new Date(today)
          weekAgo.setDate(weekAgo.getDate() - 7)
          return contentDateOnly >= weekAgo && contentDateOnly <= today
        }

        case 'month': {
          const monthAgo = new Date(today)
          monthAgo.setMonth(monthAgo.getMonth() - 1)
          return contentDateOnly >= monthAgo && contentDateOnly <= today
        }

        case 'year': {
          const yearAgo = new Date(today)
          yearAgo.setFullYear(yearAgo.getFullYear() - 1)
          return contentDateOnly >= yearAgo && contentDateOnly <= today
        }

        case 'custom': {
          if (!customStartDate && !customEndDate) return true

          const start = customStartDate ? new Date(customStartDate) : new Date(0)
          const end = customEndDate ? new Date(customEndDate) : new Date()

          return contentDateOnly >= start && contentDateOnly <= end
        }

        default:
          return true
      }
    })

    setFilteredContents(filtered)
  }

  const handleDeleteContent = async (contentId, contentTitle) => {
    try {
      setLoading(true)
      // Optimistic UI: remove item immediately
      setHomeContents(prev => prev.filter(c => c.id !== contentId))

      const deleting = deleteHomeContentCascade(contentId)
      await toast.promise(
        deleting,
        {
          loading: `Deleting "${contentTitle}"...`,
          success: `"${contentTitle}" deleted successfully.`,
          error: (err) => err?.message || 'Error deleting content.'
        }
      )
      // Refresh to ensure counts and data are correct
      await loadHomeContents()
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error(error.message || 'Error deleting content.')
    } finally {
      setLoading(false)
    }
  };

  const openConfirm = (mode, content) => setConfirmState({ open: true, mode, content })
  const closeConfirm = () => setConfirmState({ open: false, mode: null, content: null })
  const confirmAction = async () => {
    const { mode, content } = confirmState
    if (!content) return closeConfirm()
    try {
      if (mode === 'delete') {
        await handleDeleteContent(content.id, content.title)
      } else if (mode === 'edit') {
        navigate(`/home-content/${content.id}/edit`)
      }
    } finally {
      closeConfirm()
    }
  }

  const offlineFeatures = [
    {
      title: 'Practice Tests',
      description: 'Take offline practice tests without internet connection',
      icon: FileText,
      href: '/offline-tests',
      color: 'bg-blue-500'
    },
    {
      title: 'Question Bank',
      description: 'Access thousands of nursing questions offline',
      icon: BookOpen,
      href: '/questions',
      color: 'bg-green-500'
    },
    {
      title: 'Study Materials',
      description: 'Download and study nursing content offline',
      icon: BookMarked,
      href: '/nursing-bullets',
      color: 'bg-purple-500'
    },
  ]


  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="flex">
          {/* Offline Tab */}
          <button
            onClick={() => setActiveTab('offline')}
            className={`flex-1 flex items-center justify-center px-6 py-4 text-lg font-semibold transition-all duration-300 ${activeTab === 'offline'
              ? 'bg-gray-600 text-white shadow-lg'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
          >
            <WifiOff className="h-6 w-6 mr-3" />
            Offline Tests
          </button>

          {/* Online Tab */}
          <button
            onClick={() => setActiveTab('online')}
            className={`flex-1 flex items-center justify-center px-6 py-4 text-lg font-semibold transition-all duration-300 ${activeTab === 'online'
              ? 'bg-green-600 text-white shadow-lg'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
          >
            <Wifi className="h-6 w-6 mr-3" />
            Online Tests
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
        {activeTab === 'offline' && (
          <div>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Test Contents ({filteredContents.length}{filteredContents.length !== homeContents.length && ` of ${homeContents.length}`})
                </h2>
                <p className="text-gray-600">Manage offline test content and questions</p>
              </div>
              <Link
                to="/home-content/add"
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Test Content
              </Link>
            </div>

            {/* Date Filter Controls */}
            <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Date
                  </label>
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="input w-full"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last Month</option>
                    <option value="year">Last Year</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>

                {dateFilter === 'custom' && (
                  <>
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="input w-full"
                      />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="input w-full"
                      />
                    </div>
                  </>
                )}

                {dateFilter !== 'all' && (
                  <button
                    onClick={() => {
                      setDateFilter('all')
                      setCustomStartDate('')
                      setCustomEndDate('')
                    }}
                    className="btn-secondary"
                  >
                    Clear Filter
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <SkeletonOfflineTests rows={6} />
            ) : filteredContents.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-600 uppercase text-sm">
                        Test Details
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600 uppercase text-sm">
                        Tests Count
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600 uppercase text-sm">
                        Plan
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600 uppercase text-sm">
                        Discount Plan
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600 uppercase text-sm">
                        Add Test
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600 uppercase text-sm">
                        Date
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600 uppercase text-sm">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContents.map((content) => (
                      <tr key={content.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                              {content.imageUrl ? (
                                <img
                                  src={content.imageUrl}
                                  alt={content.title}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : (
                                <BookOpen className="w-6 h-6 text-gray-400" />
                              )}
                              <div className="w-full h-full bg-gray-100 flex items-center justify-center" style={{ display: 'none' }}>
                                <BookOpen className="w-6 h-6 text-gray-400" />
                              </div>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{content.title}</div>
                              <div className="text-sm text-gray-500">
                                Created: {formatDate(content.createdAt)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-left">
                          <div className="flex items-center justify-center space-x-1">
                            <FileText className="w-4 h-4 text-blue-500" />
                            <span className="font-medium text-blue-600">{content.testCount || 0}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-left">
                          <span className="text-sm text-gray-600 font-medium">
                            {content.plan || 'Free'}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-left">
                          <span className="text-sm text-gray-600 font-medium">
                            {content.discountPlan || '-'}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-left">
                          <button
                            onClick={() => navigate(`/tests-management/${content.id}`)}
                            className="p-2 bg-purple-100 text-purple-600 rounded-full hover:bg-purple-200 transition-colors"
                            title="Manage Tests"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </td>
                        <td className="py-4 px-4 text-left text-sm text-gray-600">
                          {formatDate(content.createdAt)}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={() => navigate(`/home-content/view/${content.id}`)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              title="View Content"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openConfirm('edit', content)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                              title="Edit Content"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openConfirm('delete', content)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title="Delete Content"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <FileText className="w-12 h-12 text-gray-400" />
                </div>
                {homeContents.length > 0 ? (
                  <>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                    <p className="text-gray-600 mb-4">
                      No test content matches the selected date filter.
                    </p>
                    <button
                      onClick={() => {
                        setDateFilter('all')
                        setCustomStartDate('')
                        setCustomEndDate('')
                      }}
                      className="btn-primary inline-flex items-center gap-2"
                    >
                      Clear Filter
                    </button>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No test content available</h3>
                    <p className="text-gray-600 mb-4">Create your first test content to get started with offline tests.</p>
                    <Link
                      to="/home-content/add"
                      className="btn-primary inline-flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add First Test Content
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'online' && (
          <OnlineTestManagement />
        )}
      </div>
      {/* Confirmation Modal */}
      {confirmState.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeConfirm}></div>
          <div className="relative bg-white w-full max-w-md mx-4 rounded-xl shadow-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {confirmState.mode === 'delete' ? 'Delete Content' : 'Edit Content'}
            </h3>
            <p className="text-gray-600 mb-6">
              {confirmState.mode === 'delete'
                ? `Are you sure you want to delete "${confirmState.content?.title}"? This action cannot be undone.`
                : `Do you want to edit "${confirmState.content?.title}"?`}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={closeConfirm}
                className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmAction}
                className={`px-4 py-2 rounded-md text-white ${confirmState.mode === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
              >
                {confirmState.mode === 'delete' ? 'Yes, Delete' : 'Yes, Edit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Home
